import { StateParams, StateService } from "@uirouter/angularjs";
import { IOnInit, IPromise, IWindowService, IQService } from "angular";
import { IFieldArray, IFieldConfigurationObject, ITemplateScope } from "AngularFormly";
import { FormViewController } from "../../../../app/records/forms/formView/controller";
import {
  vsName as serverUtilityName,
  ServerUtility,
} from "../../../../app/records/server/serverUtility";

import {
  FormViewService,
  vsName as FormViewServiceName,
} from "../../../../app/records/forms/formView/service";
import fieldHelpers from "../../../../app/ui-formly/fieldHelpers";

import {
  Contract,
  ContractBulkUpdateResponse,
} from "../../shared/models/contract";
import { ContractConfiguration } from "../../shared/models/contractConfiguration";
import { RecordDataSource } from "../../../records/grids";
import { ColDef } from "../../../records/grids/columns/colDef";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import { IQuery } from "../../../records/server/query";

import { vsObjectName as contractObjectName } from "../../configs/contract";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import { SelectContractModalService, vsServiceName as SelectContractModalServiceName } from "../../../Contract/shared/components/selectContractModal";
import { ColumnDefinitionService, vsName as ColumnDefinitionServiceName } from "../../../records/grids/columns/columnDefinitionService";
import { cloneDeep, uniq } from "lodash";

interface IRowCommandEventArgs<T> {
  commandName: string;
  data: T;
}

const gridId = Symbol("main.contract.pathBulk");

interface BulkUpdateFormField {
  name: string;
  displayName: string;
  fieldConfigurationObject: IFieldConfigurationObject;
}

export class ContractPatchBulkController implements IOnInit {
  static $inject = [
    FormViewServiceName,
    "$state",
    "$stateParams",
    serverUtilityName,
    GridSettingsServiceName,
    RecordGridViewServiceName,
    "$window",
    "$q",
    SelectContractModalServiceName,
    ColumnDefinitionServiceName
  ];

  readonly gridDataSource: RecordDataSource;
  readonly gridSettings: IGridSettings;
  readonly columns: ColDef[];
  defaultQuery!: IQuery;
  patchModel: any = {};

  formFields: IFieldArray = [];
  contractIds: number[] = [];
  selectedField?: BulkUpdateFormField;

  readonly modelFields: BulkUpdateFormField[] = [
    {
      name: "PropertyId",
      displayName: "Property",
      fieldConfigurationObject: fieldHelpers.selectAsyncField({
        key: "PropertyId",
        label: "PropertyId",
        queryUrl: "/api/properties",
      }),
    },
    {
      name: "ContractMachineManufacturerId",
      displayName: "Manufacturer",
      fieldConfigurationObject: fieldHelpers.selectAsyncField({
        key: "ContractMachineManufacturerId",
        label: "Manufacturer",
        queryUrl: "/api/machines/manufacturers",
      }),
    },
    {
      name: "TypeOfAgreementId",
      displayName: "Type Of Agreement",
      fieldConfigurationObject: fieldHelpers.selectAsyncField({
        key: "TypeOfAgreementId",
        label: "Type Of Agreement",
        queryUrl: "/api/contract/typesOfAgreement",
      }),
    },
    {
      name: "PerformanceGuaranteeTypeId",
      displayName: "Performance Guarantee",
      fieldConfigurationObject: fieldHelpers.selectAsyncField({
        key: "PerformanceGuaranteeTypeId",
        label: "Performance Guarantee",
        queryUrl: "/api/contract/performanceGuaranteeType",
      }),
    },
    {
      name: "FeeStructureTypeId",
      displayName: "Fee Structure",
      fieldConfigurationObject: fieldHelpers.selectAsyncField({
        key: "FeeStructureTypeId",
        label: "Fee Structure",
        queryUrl: "/api/contract/feeStructureType",
      }),
    },
    
    {
      name: "ContractNumber",
      displayName: "Contract Number",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "ContractNumber",
        label: "Contract Number",
      }),
    },
    {
      name: "ContractValue",
      displayName: "Contract Value",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "ContractValue",
        label: "Contract Value",
      }),
    },
    {
      name: "Cbr",
      displayName: "Cbr #",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "Cbr",
        label: "Cbr #",
      }),
    },
    {
      name: "PO",
      displayName: "PO",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "po",
        label: "PO",
      }),
    },
    {
      name: "ContractStartDate",
      displayName: "Contract Start",
      fieldConfigurationObject: fieldHelpers.simpleDateField({
        key: "ContractStartDate",
        label: "Contract Start",
      }),
    },
    {
      name: "ContractEndDate",
      displayName: "Contract End",
      fieldConfigurationObject: fieldHelpers.simpleDateField({
        key: "ContractEndDate",
        label: "Contract End",
      }),
    },
    {
      name: "Notes",
      displayName: "Notes",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "Notes",
        label: "Notes",
      }),
    },
    {
      name: "Requisition",
      displayName: "Requisition",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "Requisition",
        label: "Requisition",
      }),
    },
    {
      name: "Details",
      displayName: "Details",
      fieldConfigurationObject: fieldHelpers.simpleStringField({
        key: "Details",
        label: "Details",
      }),
    },
    {
      name: "OwnedLeased",
      displayName: "Owned/Leased",
      fieldConfigurationObject: fieldHelpers.selectField({
        key: "OwnedLeased",
        label: "Owned/Leased",
        options: [
          { name: 'Leased', value: 'Leased' },
          { name: 'Owned', value: 'Owned' },
        ],
      }),
    },
    {
      name: "Locked",
      displayName: "Locked",
      fieldConfigurationObject: fieldHelpers.simpleBoolField({
        key: "Locked",
        label: "Locked",
        defaultValue: false,
      }),
    },
  ];

  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    public readonly $stateParams: StateParams,
    public readonly serverUtility: ServerUtility,
    gridSettingsService: GridSettingsService,
    public readonly recordGridViewService: RecordGridViewService,
    private readonly $window: IWindowService,
    private readonly $q: IQService,
    private readonly selectContractModalService: SelectContractModalService,
    private readonly columnDefinitionService: ColumnDefinitionService
  ) {
    const gridConfiguration =
      recordGridViewService.getGridConfiguration(contractObjectName);
    this.columns = gridConfiguration?.columns ?? [];

    this.gridDataSource = gridConfiguration?.gridDataSource!;
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.gridSettings.saveQuery({}); //initialize saved query to empty.  was saving previously selected Ids
  }

  $onInit(): void {
    this.defaultQuery = { Ids: this.contractIds }
  }

  submit(formViewController: FormViewController): IPromise<void> {
    const requestData = this.getRequestData(this.patchModel);

    const serverRequest =
      this.formViewService.submitForm<ContractBulkUpdateResponse>({
        url: `/api/contract/bulk-update`,
        formObject: requestData,
        formViewController,
        method: "PATCH",
      });

    return serverRequest.then((contractUpdateResponse: any) => {
      this.$state.go("main.contract.bulklog.results", { logId: contractUpdateResponse.logId });
    });
  }

  addField(fieldInfo?: BulkUpdateFormField) {
    this.selectedField = undefined;
    if (!fieldInfo) return;
    const existingField = this.findExistingField(fieldInfo.name);
    if (existingField) return;
  
    const newInputField = cloneDeep(fieldInfo.fieldConfigurationObject);
  
    const newButtonField: IFieldConfigurationObject = {
      template: `
      <div>
  <div class="pull-right">
      <div
      class="btn btn-danger game-config-btn"
      ng-click="deleteRow(options.data.fieldName)"      
    >
      <i class="glyphicon glyphicon-remove"></i>
    </div></div></div>`,
    controller: buttonFieldController,
    wrapper: ["bootstrapLabel", "bootstrapHasError"],
    data: { fieldName: fieldInfo.name, patchBulkController: this },
    templateOptions: {label: " "}
    }

    newInputField.className = "col-xs-11"
    newButtonField.className = "col-xs-1"

    const wrapper = {
      className: "row",
      fieldGroup: [
        newInputField,
        newButtonField
      ],
      data: { fieldName: fieldInfo.name },
    }
  
    this.formFields.push(wrapper);
  }
  

  removeField(fieldName: string) {
    const field = this.findExistingField(fieldName)
    if(!field) return;

    this.formFields = this.formFields.filter(f => f !== field);
    delete this.patchModel[fieldName]
  }

  getAvailableFields(): BulkUpdateFormField[] {
    return this.modelFields.filter(field => !this.findExistingField(field.name));
  }  

  private findExistingField(name: string) {
    return this.formFields.find((f) => f.data?.fieldName === name);
  }

  getRequestData(model: any) {
    const serverRequest = {
      Ids: this.contractIds,
      Patch: {
        ...model,
        Reset: [],
      },
    };

    this.formFields.forEach((field: any) => {
      const fieldValue = model[field.data.fieldName]; 
      const isValueEmpty = this.isFieldValueEmpty(fieldValue);
  
      if (isValueEmpty) {
        serverRequest.Patch.Reset.push(field.data.fieldName);
        this.patchModel.Reset = [field.data.fieldName];
        
        delete serverRequest.Patch[field.data.fieldName];
      }
    });
    return serverRequest;
  }
  
  private isFieldValueEmpty(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true; 
    }

    return false;
  }

  cancel(): IPromise<void> {
    return this.$state.go("^.grid").then();
  }

  private showErrorMessage(message: string) {
    this.$window.alert(`Error(s) occurred: ${message}`);
  }

  openSelectContractModal() {
    const gridConfiguration = this.recordGridViewService.getGridConfiguration(contractObjectName)!;

    gridConfiguration.columns = [
      this.columnDefinitionService.getCommandColDef({
        headerName: "select",
        iconClass: "fa fa-plus-circle text-primary",
        name: "select",
      }),
      ...(gridConfiguration.columns ?? []),
    ];

    gridConfiguration.onItemCommand = (
      args: IRowCommandEventArgs<ContractConfiguration>
    ) => {
      const { commandName, data: contractConfiguration } = args;
      if (commandName === "select") {
        modalInstance.close(contractConfiguration);
      }
      return this.$q.when();
    };

    const modalInstance = this.selectContractModalService.openModal({
      gridConfiguration,
    });

    return modalInstance.result
      .then((result: ContractConfiguration) => {
        if (result && result.Id) {
          this.contractIds = uniq([...this.contractIds, result.Id]);
          this.defaultQuery.Ids = this.contractIds;
        }
        return result;
      })
      .catch((error) => {
        console.error("Modal closed with error:", error);
      });
  }
}

class buttonFieldController {
  static $inject = ["$scope"]
  constructor(private readonly $scope: ITemplateScope){
    const s = <any>$scope
    s.deleteRow = (fieldName: string) => this.deleteRow(fieldName)
  }

  deleteRow(fieldName: string){
    this.$scope.options.data.patchBulkController.removeField(fieldName)
  }
}
